import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Settings, Image as ImageIcon, X, Save, AlertCircle } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_URL } from '../../api/config';

const SettingsPage = () => {
  const { userToken: token } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    restaurantName: '',
    phoneNumber: '',
    gstin: '',
    businessEmail: '',
    location: '',
    logoUrl: '',
    note: 'Thank you Visit Again'
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        if (data.logoUrl) {
          setLogoPreview(data.logoUrl);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching settings:', errorData.message);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!settings.restaurantName?.trim()) {
      newErrors.restaurantName = 'Restaurant name is required';
    }
    
    if (!settings.phoneNumber?.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\d{10}$/.test(settings.phoneNumber.trim())) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
    }
    
    if (settings.businessEmail?.trim() && 
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(settings.businessEmail.trim())) {
      newErrors.businessEmail = 'Please enter a valid email address';
    }
    
    if (settings.gstin?.trim() && 
        !/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/.test(settings.gstin.trim())) {
      newErrors.gstin = 'Please enter a valid GSTIN';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const pickImage = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          setErrors(prev => ({
            ...prev,
            logo: 'File size should be less than 5MB'
          }));
          return;
        }
        
        setSelectedLogo(file);
        setLogoPreview(URL.createObjectURL(file));
        setErrors(prev => ({ ...prev, logo: '', submit: '' }));
      }
    };
    input.click();
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSaving(true);
    setErrors(prev => ({ ...prev, submit: '' }));
  
    const formData = new FormData();
    formData.append('restaurantName', settings.restaurantName.trim());
    formData.append('phoneNumber', settings.phoneNumber.trim());
    formData.append('gstin', settings.gstin?.trim() || '');
    formData.append('businessEmail', settings.businessEmail?.trim() || '');
    formData.append('location', settings.location?.trim() || '');
    formData.append('note', settings.note?.trim() || 'Thank you Visit Again');
    
    if (selectedLogo) {
      formData.append('logo', selectedLogo);
    }
  
    try {
      const response = await fetch(`${API_URL}/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });
  
      const data = await response.json();
  
      if (response.ok) {
        setSettings(data);
        toast.success('Settings saved successfully!');
      } else {
        setErrors(prev => ({
          ...prev,
          submit: data.message || 'Failed to save settings'
        }));
        toast.error(data.message || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setErrors(prev => ({
        ...prev,
        submit: 'Failed to save settings. Please try again.'
      }));
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (name, value) => {
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
        submit: ''
      }));
    }
  };

  const handleRemoveLogo = useCallback(() => {
    setLogoPreview('');
    setSelectedLogo(null);
    setSettings(prev => ({ ...prev, logoUrl: '' }));
    setErrors(prev => ({ ...prev, logo: '', submit: '' }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <ToastContainer position="top-right" autoClose={5000} />
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-2">
            <Settings className="text-gray-500" size={24} />
            <h1 className="text-2xl font-bold text-gray-800">Restaurant Settings</h1>
          </div>
          <p className="text-gray-500">Manage your restaurant's information and preferences</p>
        </div>

        {/* Error Message */}
        {errors.submit && (
          <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 border-l-4 border-red-500">
            <AlertCircle className="w-5 h-5" />
            <span>{errors.submit}</span>
          </div>
        )}

        <div className="p-6 space-y-6">
          {/* Logo Section */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Restaurant Logo</label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={pickImage}
                className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
              >
                <ImageIcon className="text-gray-500" size={20} />
                <span className="text-gray-500">
                  {logoPreview ? 'Change Logo' : 'Upload Logo'}
                </span>
              </button>
              {logoPreview && (
                <div className="relative">
                  <img src={logoPreview} alt="Logo preview" className="w-20 h-20 rounded-lg object-cover" />
                  <button
                    onClick={handleRemoveLogo}
                    className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 hover:bg-red-600 transition-colors"
                  >
                    <X className="text-white" size={16} />
                  </button>
                </div>
              )}
            </div>
            {errors.logo && <p className="text-sm text-red-500">{errors.logo}</p>}
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Restaurant Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.restaurantName ? 'border-red-500' : 'border-gray-300'
                }`}
                value={settings.restaurantName}
                onChange={(e) => handleChange('restaurantName', e.target.value)}
                placeholder="Enter restaurant name"
              />
              {errors.restaurantName && (
                <p className="text-sm text-red-500">{errors.restaurantName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                }`}
                value={settings.phoneNumber}
                onChange={(e) => handleChange('phoneNumber', e.target.value)}
                placeholder="10-digit phone number"
              />
              {errors.phoneNumber && (
                <p className="text-sm text-red-500">{errors.phoneNumber}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GSTIN (Optional)
              </label>
              <input
                type="text"
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.gstin ? 'border-red-500' : 'border-gray-300'
                }`}
                value={settings.gstin}
                onChange={(e) => handleChange('gstin', e.target.value)}
                placeholder="Enter GSTIN number"
              />
              {errors.gstin && <p className="text-sm text-red-500">{errors.gstin}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Email (Optional)
              </label>
              <input
                type="email"
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.businessEmail ? 'border-red-500' : 'border-gray-300'
                }`}
                value={settings.businessEmail}
                onChange={(e) => handleChange('businessEmail', e.target.value)}
                placeholder="Enter business email"
              />
              {errors.businessEmail && (
                <p className="text-sm text-red-500">{errors.businessEmail}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location (Optional)
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={settings.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="Enter restaurant location"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Receipt Note
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md h-24"
                value={settings.note}
                onChange={(e) => handleChange('note', e.target.value)}
                placeholder="Enter a note to display on receipts"
              />
            </div>
          </div>

          {/* Save Button */}
          <div className="pt-6 border-t border-gray-200">
            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-white ${
                isSaving ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
              }`}
              onClick={handleSubmit}
              disabled={isSaving}
            >
              <Save size={18} />
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>

          {/* Delete Account Button */}
          <button
            className="w-full mt-6 py-3 px-4 bg-red-600 hover:bg-red-700 rounded-md text-white font-medium"
            onClick={() => navigate('/AccountDelete')}
          >
            Delete Account
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
